import { useState, type ReactNode } from "react";
import { Badge, Button, Card, Eyebrow, Icon, Tag } from "../../ds/DS";
import { isOpen, type VProject } from "../data";
import { currentRelease, projectMilestones } from "./util";
import type { PageProps } from "../Portal";

export function PageHead({
  eyebrow,
  title,
  sub,
  actions,
}: {
  eyebrow: string;
  title: string;
  sub?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="pg-head">
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="pg-title">{title}</h1>
        {sub ? <p className="pg-sub">{sub}</p> : null}
      </div>
      {actions ? <div className="row-8">{actions}</div> : null}
    </div>
  );
}

/** Overview stat card: clickable; the gradient rule shows only when it needs attention. */
export function StatCard({
  label,
  value,
  note,
  hot,
  bad,
  onClick,
}: {
  label: string;
  value: ReactNode;
  note: string;
  hot: boolean; // show the 48px gradient rule
  bad?: boolean; // note in red
  onClick: () => void;
}) {
  return (
    <button type="button" className={"stat-card" + (hot ? " is-hot" : "")} onClick={onClick}>
      <span className="stat-label">{label}</span>
      <span className="stat-card-value">{value}</span>
      <span className={"stat-card-note" + (bad ? " is-bad" : "")}>{note}</span>
    </button>
  );
}

export function ScopeNote({
  pid,
  setPid,
  projectName,
}: {
  pid: string;
  setPid: (id: string) => void;
  projectName: (id: string) => string;
}) {
  if (pid === "all") return null;
  return (
    <div className="scope-note">
      <Icon name="folder" size={14} />
      <span>
        Showing <strong>{projectName(pid)}</strong> only.
      </span>
      <button className="link-btn" onClick={() => setPid("all")}>
        Show all projects
      </button>
    </div>
  );
}

/** Progress bar with the percentage (or any label) beside it. */
export function Progress({ pct, label }: { pct: number; label?: string }) {
  return (
    <div className="prog">
      <div className="prog-track">
        <div className="prog-fill" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
      <span className={label ? "prog-label" : "prog-pct"}>{label ?? `${pct}%`}</span>
    </div>
  );
}

/** Tab strip with count pills (the DS Tabs), plus optional text on the right. */
export function TabBar({
  items,
  value,
  onChange,
  aside,
}: {
  items: { value: string; label: string; count: number }[];
  value: string;
  onChange: (v: string) => void;
  aside?: ReactNode;
}) {
  return (
    <div className="tabbar">
      <div className="ds-tabs" role="tablist">
        {items.map((it) => (
          <button
            key={it.value}
            role="tab"
            aria-selected={value === it.value}
            className={`ds-tab ${value === it.value ? "is-on" : ""}`}
            onClick={() => onChange(it.value)}
          >
            {it.label}
            <span className="ds-tab-count">{it.count}</span>
          </button>
        ))}
      </div>
      {aside ? <span className="tabbar-aside mono">{aside}</span> : null}
    </div>
  );
}

/**
 * Dialog footer: an optional red "Delete…" link on the left (asks once
 * inline), then Cancel and the primary action with an arrow.
 */
export function DialogFoot({
  saveLabel,
  busy,
  invalid,
  onCancel,
  onSave,
  deleteLabel,
  deleteWhat,
  onDelete,
}: {
  saveLabel: string;
  busy: boolean;
  invalid?: boolean;
  onCancel: () => void;
  onSave: () => void;
  deleteLabel?: string; // e.g. "Delete task"
  deleteWhat?: string; // e.g. the item's name, for the confirm line
  onDelete?: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="dlg-foot">
      {onDelete ? (
        confirm ? (
          <span className="row-8 dlg-del">
            <span>{deleteWhat ? `Delete ${deleteWhat}?` : "Delete this?"}</span>
            <button className="dlg-del-btn" onClick={onDelete} disabled={busy}>
              {deleteLabel ?? "Delete"}
            </button>
            <button className="link-btn" onClick={() => setConfirm(false)}>
              Keep it
            </button>
          </span>
        ) : (
          <button className="dlg-del-btn" onClick={() => setConfirm(true)}>
            {(deleteLabel ?? "Delete") + "…"}
          </button>
        )
      ) : null}
      <span style={{ flex: 1 }} />
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button arrow onClick={onSave} disabled={busy || invalid}>
        {busy ? "Saving…" : saveLabel}
      </Button>
    </div>
  );
}

/**
 * Project card (Overview and Projects). `full` adds the stack tags and the
 * link row with counts (plus Edit project for admins).
 */
export function ProjectCard({
  p,
  full,
  ctx,
  onEdit,
}: {
  p: VProject;
  full: boolean;
  ctx: PageProps;
  onEdit?: () => void;
}) {
  const {
    isAdmin,
    tickets,
    milestones,
    releases,
    tasks,
    changeRequests,
    documents,
    credentials,
    go,
    setPid,
    openItem,
  } = ctx;
  const openT = tickets.filter((t) => t.projectId === p.id && isOpen(t));
  const waiting = isAdmin
    ? openT.filter((t) => t.needsReply).length
    : openT.filter((t) => t.status === "Waiting on client").length;
  const ms = projectMilestones(milestones, p.id);
  const nextM = ms.find((m) => m.state === "active") ?? ms.find((m) => m.state === "next");
  const rel = currentRelease(releases, p.id);
  const at = (page: string) => () => {
    setPid(p.id);
    go(page);
  };
  const meta = [
    p.due && `Due ${p.due}`,
    p.raw.hoursBudget != null ? `${p.raw.hoursUsed ?? 0} / ${p.raw.hoursBudget}h used` : "",
    p.lead && `Lead ${p.lead}`,
  ]
    .filter(Boolean)
    .join(" · ");
  const facts = [
    {
      label: "Next milestone",
      value: nextM ? nextM.name : "None planned",
      sub: nextM ? `${nextM.date || "No date"} →` : "Milestones →",
      go: at("milestones"),
    },
    {
      label: "Release",
      value: rel ? rel.name : "None open",
      sub: rel ? `${rel.status} →` : "Releases →",
      go: rel ? () => openItem("releases", rel.id) : () => go("releases"),
    },
    {
      label: "Open tickets",
      value: String(openT.length),
      sub: waiting ? `${waiting} ${isAdmin ? "need a reply" : "waiting on you"} →` : "Tickets →",
      go: at("tickets"),
    },
  ];
  const links = [
    {
      label: "Tasks",
      icon: "clipboard-check",
      page: "tasks",
      n: tasks.filter((t) => t.projectId === p.id && t.state !== "Done" && t.state !== "Cancelled")
        .length,
    },
    {
      label: "Change requests",
      icon: "git-branch",
      page: "changes",
      n: changeRequests.filter((c) => c.projectId === p.id).length,
    },
    {
      label: "Documents",
      icon: "folder",
      page: "files",
      n: documents.filter((d) => d.projectId === p.id).length,
    },
    {
      label: "Credentials",
      icon: "shield-check",
      page: "vault",
      n: credentials.filter((c) => c.projectId === p.id).length,
    },
  ];
  return (
    <Card accent accentWidth={48} padding="22px 24px" className="pcard">
      <div className="pcard-head">
        <div>
          <div className="pcard-code mono">{p.code}</div>
          <div className={"pcard-name" + (full ? " is-full" : "")}>{p.name}</div>
        </div>
        <div className="row-6 pcard-badges">
          <Badge tone="navy">{p.status}</Badge>
          <Badge tone={p.health === "at-risk" ? "warning" : "teal"} dot>
            {p.health === "at-risk" ? "At risk" : "On track"}
          </Badge>
        </div>
      </div>
      {p.summary ? <p className="pcard-summary">{p.summary}</p> : null}
      <Progress pct={p.progress} />
      {meta ? <div className="pcard-meta mono">{meta}</div> : null}
      {p.risk ? (
        <div className="pcard-risk">
          <Icon name="triangle-alert" size={14} />
          <span>{p.risk}</span>
        </div>
      ) : null}
      <div className="pcard-facts">
        {facts.map((f) => (
          <button key={f.label} type="button" className="pcard-fact" onClick={f.go}>
            <span className="pcard-fact-k">{f.label}</span>
            <span className="pcard-fact-v">{f.value}</span>
            <span className="pcard-fact-sub">{f.sub}</span>
          </button>
        ))}
      </div>
      {full ? (
        <>
          {p.stack.length ? (
            <div className="row-6">
              {p.stack.map((s) => (
                <Tag key={s}>{s}</Tag>
              ))}
            </div>
          ) : null}
          <div className="pcard-links">
            {links.map((l) => (
              <button key={l.page} type="button" className="pcard-link" onClick={at(l.page)}>
                <Icon name={l.icon} size={14} />
                {l.label}
                <span className="mono">{l.n}</span>
              </button>
            ))}
            {isAdmin && onEdit ? (
              <span className="pcard-edit">
                <Button variant="secondary" size="sm" icon="pencil" onClick={onEdit}>
                  Edit project
                </Button>
              </span>
            ) : null}
          </div>
        </>
      ) : null}
    </Card>
  );
}

